import { useEffect, useState } from 'react';
import { getPlatform, type Platform } from '../utils/native';

const PLATFORM_INFO = {
  web: {
    isWeb: true,
    isNative: false,
    isIOS: false,
    isAndroid: false,
  },
  ios: {
    isWeb: false,
    isNative: true,
    isIOS: true,
    isAndroid: false,
  },
  android: {
    isWeb: false,
    isNative: true,
    isIOS: false,
    isAndroid: true,
  },
} as const;

const UNDEFINED_PLATFORM = {
  isWeb: false,
  isNative: false,
  isIOS: false,
  isAndroid: false,
} as const;

/**
 * Platform flags for use during render. In event handlers, effects and
 * non-React modules, call `getPlatform` / `isWeb` / `isNative` / `isIOS` /
 * `isAndroid` from `utils/native` directly instead.
 *
 * The platform is read in an effect, not during render: our HTML is built
 * ahead of time in Node, where Capacitor always reports `web`, so reading it
 * during render would bake that answer into the markup and it would be wrong
 * on a device. Effects don't run while the HTML is being built or adopted, so
 * the first render can't disagree with it.
 *
 * That costs a render on every mount, not just at startup — `useState` begins
 * at `undefined` for each new component instance however long the app has been
 * running. Every flag is `false` until the platform is known, so components
 * that depend on it should render nothing until one of them is `true` rather
 * than treating "not known yet" as a particular platform.
 */
export const usePlatformInfo = () => {
  const [platform, setPlatform] = useState<Platform>();

  useEffect(() => {
    setPlatform(getPlatform());
  }, []);

  return platform ? PLATFORM_INFO[platform] : UNDEFINED_PLATFORM;
};
